import React, { useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import DraggableQuestionList from "./DraggableQuestionList";

const TestBuilderLayout = ({
  builder,
  onSubmit,
  mode = "create",
}) => {
  const {
    testName, setTestName,
    testDescription, setTestDescription,
    testInstructions, setTestInstructions,
    testTimeLimit, setTestTimeLimit,
    questions,
    categories,
    gradeRules,
    isPrivate, setIsPrivate,
    modifications,     
    setModifications,  
    addQuestion,
    deleteQuestion,
    addOption,
    updateQuestion,
    updateOption,
    deleteOption,
    handleImageUpload,
    addNewCategory,

    updateGradeRule,
    addGradeRule,
    removeGradeRule,

    saveDraft,
    loadDraft,
    deleteDraft,
    savedDrafts,
    draftId,

  } = builder;

  const [showDraftModal, setShowDraftModal] = useState(false);

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto text-black">

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6 h-[calc(100vh-3rem)]">

        {/* LEFT PANEL */}
        <div className="bg-white shadow-md rounded-lg p-6 overflow-y-auto pb-32 lg:pb-6">

          <h2 className="text-2xl font-bold mb-4">
            {mode === "edit" ? "Edit Test" : "Create Test"}
          </h2>

          {/* EDITING DRAFT BANNER */}
          {draftId && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              Editing Draft: <b>{testName || "Untitled Test"}</b>
            </div>
          )}

          {/* TEST NAME */}
          <h3 className="text-lg font-semibold mb-2">Test Name</h3>
          <input
            className="w-full p-2 border rounded mb-4"
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
            placeholder="Test Name"
          />

          {/* DESCRIPTION */}
          <h3 className="text-lg font-semibold mb-2">Description</h3>
          <textarea
            className="w-full p-2 border rounded mb-4"
            value={testDescription}
            onChange={(e) => setTestDescription(e.target.value)}
            placeholder="Description"
          />

          {/* INSTRUCTIONS */}
          <h3 className="text-lg font-semibold mb-2">Instruction</h3>
          <textarea
            className="w-full p-2 border rounded mb-4"
            value={testInstructions}
            onChange={(e) => setTestInstructions(e.target.value)}
            placeholder="Instructions"
          />

          {/* TIME LIMIT */}
          <h3 className="text-lg font-semibold mb-2">Time Limit</h3>
          <input
            type="number"
            className="w-full p-2 border rounded mb-4"
            value={testTimeLimit}
            onChange={(e) => setTestTimeLimit(e.target.value)}
            placeholder="Time Limit"
          />

          {/* PRIVATE TOGGLE */}
          <div className="mb-6 flex items-center justify-between p-4 border rounded-xl bg-gray-50">
            <div>
              <h3 className="font-semibold">Private Test</h3>
            </div>

            <button
              onClick={() => setIsPrivate(!isPrivate)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                isPrivate ? "bg-purple-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white ${
                  isPrivate ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* QUESTIONS */}
          <h3 className="text-lg font-semibold mb-4">Questions</h3>

          <DraggableQuestionList
            questions={questions}
            setQuestions={builder.setQuestions}
            addQuestion={addQuestion}
            deleteQuestion={deleteQuestion}
            addOption={addOption}
            updateQuestion={updateQuestion}
            updateOption={updateOption}
            handleImageUpload={handleImageUpload}
            categories={categories}
            addNewCategory={addNewCategory}
            deleteOption={deleteOption}
          />

          {/* GRADE RULES */}
          <div className="mb-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">Grade / Result Ranges</h3>

            {gradeRules.map((rule, index) => (
              <div key={index} className="border p-3 rounded mb-3 bg-gray-50">

                <div className="flex flex-col lg:flex-row gap-3 mb-2">

                  <input
                    type="number"
                    className="p-2 border rounded lg:w-1/3"
                    placeholder="Threshold"
                    value={rule.threshold}
                    onChange={(e) =>
                      updateGradeRule(
                        index,
                        "threshold",
                        parseInt(e.target.value) || ""
                      )
                    }
                  />

                  <input
                    className="p-2 border rounded lg:w-2/3"
                    placeholder="Label (e.g. Low, Moderate, High)"
                    value={rule.label}
                    onChange={(e) =>
                      updateGradeRule(index, "label", e.target.value)
                    }
                  />

                  <button
                    onClick={() => removeGradeRule(index)}
                    className="text-red-500 hover:text-red-700 text-xl flex items-center justify-center"
                  >
                    <MdDeleteOutline />
                  </button>

                </div>

                <textarea
                  className="w-full p-2 border rounded"
                  placeholder="Result description (what this score means)"
                  value={rule.text}
                  onChange={(e) =>
                    updateGradeRule(index, "text", e.target.value)
                  }
                />

              </div>
            ))}

            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={addGradeRule}
            >
              + Add Grade Range
            </button>

          </div>

        {/* SPECIAL MODIFICATIONS */}
            <h3 className="text-lg font-semibold mb-4">Special Modifications</h3>
        {/* HIDE MARKS TOGGLE */}
        <div className="mb-4 flex items-center justify-between p-4 border rounded-xl bg-gray-50">
          <div>
            <h3 className="font-semibold">Hide Marks</h3>
            <p className="text-gray-500 text-sm">
              Toggle to hide marks in the test results for users.
            </p>
          </div>

          <button
            onClick={() =>
              builder.setModifications({
                ...builder.modifications,
                hideMarks: !builder.modifications?.hideMarks,
              })
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
              builder.modifications?.hideMarks ? "bg-purple-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white ${
                builder.modifications?.hideMarks ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        </div>


        {/* RIGHT PANEL DESKTOP */}
        <div className="hidden lg:block">
          <div className="h-full flex flex-col gap-3 bg-white shadow-md rounded-lg p-4 border">

            <button
              className="bg-blue-600 text-white px-4 py-3 rounded"
              onClick={() => addQuestion()}
            >
              + Add Question
            </button>

            <button
              className="bg-purple-600 text-white px-4 py-3 rounded"
              onClick={onSubmit}
            >
              {mode === "edit" ? "Update Test" : "Submit Test"}
            </button>

            <button
              className="bg-green-600 text-white px-4 py-3 rounded"
              onClick={saveDraft}
            >
              Save Draft
            </button>

            <button
              className="bg-gray-800 text-white px-4 py-3 rounded"
              onClick={() => setShowDraftModal(true)}
            >
              Load Draft
            </button>

          </div>
        </div>
      </div>

      {/* MOBILE BOTTOM ACTION BAR */}
      <div className="lg:hidden fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur border-t shadow-lg p-3 flex gap-2 z-40">

        <button
          className="flex-1 bg-blue-600 text-white py-3 rounded"
          onClick={() => addQuestion()}
        >
          + Question
        </button>

        <button
          className="flex-1 bg-purple-600 text-white py-3 rounded"
          onClick={onSubmit}
        >
          {mode === "edit" ? "Update" : "Submit"}
        </button>

        <button
          className="flex-1 bg-green-600 text-white py-3 rounded"
          onClick={saveDraft}
        >
          Save
        </button>

        <button
          className="flex-1 bg-gray-800 text-white py-3 rounded"
          onClick={() => setShowDraftModal(true)}
        >
          Drafts
        </button>

      </div>

      {/* DRAFT MODAL */}
      {showDraftModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">

          <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">

            <h3 className="text-xl font-bold mb-4">Saved Drafts</h3>

            {savedDrafts.length === 0 && <p>No drafts found</p>}

            {savedDrafts.map((d) => (
              <div
                key={d.id}
                className="border p-3 rounded flex justify-between mb-2"
              >
                <div>
                  <b>{d.testName}</b>
                  <p className="text-sm text-gray-500">
                    {new Date(d.lastModified).toLocaleString()}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    className="bg-blue-500 text-white px-3 py-1 rounded"
                    onClick={() => {
                      loadDraft(d);
                      setShowDraftModal(false);
                    }}
                  >
                    Load
                  </button>

                  <button
                    className="bg-red-500 text-white px-3 py-1 rounded"
                    onClick={() => deleteDraft(d.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}

            <div className="mt-4 flex justify-end">
              <button
                className="bg-gray-300 px-4 py-2 rounded"
                onClick={() => setShowDraftModal(false)}
              >
                Close
              </button>
            </div>

          </div>

        </div>
      )}
    </div>
  );
};

export default TestBuilderLayout;